import React from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Avatar, Collapse, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper } from '@mui/material';

import { useBooleanStateWithToggle } from '@paragon/client-tools';
import { InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { ProgressAvatar } from './analyze/ProgressAvatar';

export type ProgressAvatarValue =
  number | 'indeterminate' | 'hourglass' | 'success' | 'success-with-warnings' | 'failure';

export interface ProgressProblem {
  type: 'warning' | 'error';
  message: string;
}

export type ProgressCategory = 'waiting' | 'working' | 'finished';

export interface ComponentDesignInProgress {
  name: string;
  statusMessage: string;

  problems: ProgressProblem[];
  category: ProgressCategory;
}

export function ComponentDesignListItem(props: ComponentDesignInProgress) {
  const [isExpanded, toggleExpansion] = useBooleanStateWithToggle();

  const hasProblems = props.problems.length > 0;

  const getAvatarValue = (): ProgressAvatarValue => {
    switch (props.category) {
      case 'waiting':
        return 'hourglass';

      case 'working':
        return 'indeterminate';

      case 'finished': {
        if (props.problems.some((problem) => problem.type === 'error')) return 'failure';
        if (props.problems.some((problem) => problem.type === 'warning')) return 'success-with-warnings';
        return 'success';
      }

      default:
        assertExhaustiveSwitch(props.category);
    }
  };

  const listItemContents = (
    <>
      <ListItemAvatar>
        <Avatar>
          <ProgressAvatar value={getAvatarValue()} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={props.name} secondary={props.statusMessage} />
      {hasProblems && <ExpandMore />}
    </>
  );

  return (
    <>
      {hasProblems ? (
        <ListItemButton onClick={toggleExpansion}>{listItemContents}</ListItemButton>
      ) : (
        <ListItem>{listItemContents}</ListItem>
      )}

      {hasProblems && (
        <Collapse in={isExpanded} unmountOnExit>
          <ListItem>
            <Paper sx={{ width: '100%' }}>
              {Object.values(props.problems).map(({ type, message }) => (
                <InlineNotification key={message} fullWidthWrap type={type}>
                  {message}
                </InlineNotification>
              ))}
            </Paper>
          </ListItem>
        </Collapse>
      )}
    </>
  );
}
